import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { addYears } from 'date-fns';
import { Subject, takeUntil } from 'rxjs';

export enum DateTimeInputType {
  Date = 'date',
  Time = 'time',
  DateTime = 'dateTime',
  DateTimeWithSeconds = 'dateTimeWithSeconds',
}

@Component({
  selector: 'fl-date-time-input',
  templateUrl: './date-time-input.component.html',
  styleUrls: ['./date-time-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class DateTimeInputComponent implements OnInit, OnDestroy {
  @Input()
  public inputType: DateTimeInputType;
  @Input()
  public inputControl: UntypedFormControl;
  @Input()
  public min = new Date('1900-01-01');
  @Input()
  public max = addYears(new Date(), 20);
  @Output()
  public valueChange = new EventEmitter();
  @Output()
  public blurEvent = new EventEmitter();
  @Output()
  public focusEvent = new EventEmitter();

  public value: string;
  public isValidDateTime = true;
  public displayDateInput = false;
  public displayTimeInput = false;
  public displayDateTimeInput = false;
  public displayDateTimeWithSecondsInput = false;

  public dateMask = '99/99/9999';
  public datePlaceholder = 'month/day/year';
  public timeMask = '99:99';
  public timePlaceholder = 'hour:minute';
  public dateTimeMask = '99/99/9999 99:99';
  public dateTimePlaceholder = 'month/day/year hour:minute';
  public dateTimeWithSecondsMask = '99/99/9999 99:99:99';
  public dateTimeWithSecondsPlaceholder = 'month/day/year hour:minute:seconds';

  private _onDestroy$ = new Subject();

  constructor() {}

  public ngOnInit() {
    if (this.inputControl == null) {
      throw new Error('input control cannot be null');
    }
    this.setValueFromControl(this.inputControl.value);

    this.inputControl.valueChanges.pipe(takeUntil(this._onDestroy$)).subscribe((value) => {
      this.setValueFromControl(value);
    });
  }

  public ngOnDestroy() {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }

  public dateValidator() {
    if (this.value != null) {
      const month = parseInt(this.value.slice(0, 2), 10);
      const day = parseInt(this.value.slice(2, 4), 10);
      const year = parseInt(this.value.slice(4, 8), 10);
      const date = new Date(year, month - 1, day, 0, 0);
      if (
        this.value.length === 8 &&
        year > 1900 &&
        month <= 12 &&
        day <= 31 &&
        date > this.min &&
        date < this.max
      ) {
        this.setValidDate(date);
      } else {
        this.setInvalidDate();
      }
      if (this.value.length === 0) {
        this.isValidDateTime = true;
        this.inputControl.clearValidators();
      }
    } else {
      this.resetValidity();
    }
    this.inputControl.updateValueAndValidity();
  }

  public timeValidator() {
    if (this.value != null) {
      const hour = parseInt(this.value.slice(0, 2), 10);
      const minute = parseInt(this.value.slice(2, 4), 10);
      if (this.value.length === 4 && hour <= 23 && minute <= 59) {
        this.isValidDateTime = true;
        const year = new Date(Date.now()).getFullYear();
        const month = new Date(Date.now()).getMonth();
        const day = new Date(Date.now()).getDate();
        const date = new Date(year, month, day, hour, minute);
        this.updateInputControl(date);
        this.valueChange.emit(date);
      } else {
        this.setInvalidDate();
      }
      if (this.value.length === 0) {
        this.isValidDateTime = true;
        this.inputControl.clearValidators();
      }
    } else {
      this.resetValidity();
    }
    this.inputControl.updateValueAndValidity();
  }

  public dateTimeValidator() {
    if (this.value != null) {
      const month = parseInt(this.value.slice(0, 2), 10);
      const day = parseInt(this.value.slice(2, 4), 10);
      const year = parseInt(this.value.slice(4, 8), 10);
      const hour = parseInt(this.value.slice(8, 10), 10);
      const minute = parseInt(this.value.slice(10, 12), 10);
      const date = new Date(year, month - 1, day, hour, minute);
      if (
        this.value != null &&
        this.value.length === 12 &&
        year > 1900 &&
        month <= 12 &&
        day <= 31 &&
        hour <= 23 &&
        minute <= 59 &&
        date > this.min &&
        date < this.max
      ) {
        this.setValidDate(date);
      } else {
        this.setInvalidDate();
      }
      if (this.value.length === 0) {
        this.resetValidity();
      }
    } else {
      this.resetValidity();
    }
    this.inputControl.updateValueAndValidity();
  }

  public dateTimeWithSecondsValidator() {
    if (this.value != null) {
      const month = parseInt(this.value.slice(0, 2), 10);
      const day = parseInt(this.value.slice(2, 4), 10);
      const year = parseInt(this.value.slice(4, 8), 10);
      const hour = parseInt(this.value.slice(8, 10), 10);
      const minute = parseInt(this.value.slice(10, 12), 10);
      const seconds = parseInt(this.value.slice(12, 14), 10);
      const date = new Date(year, month - 1, day, hour, minute, seconds);
      if (
        this.value != null &&
        this.value.length === 14 &&
        year > 1900 &&
        month <= 12 &&
        day <= 31 &&
        hour <= 23 &&
        minute <= 59 &&
        seconds <= 59 &&
        date > this.min &&
        date < this.max
      ) {
        this.setValidDate(date);
      } else {
        this.setInvalidDate();
      }
      if (this.value.length === 0) {
        this.resetValidity();
      }
    } else {
      this.resetValidity();
    }
    this.inputControl.updateValueAndValidity();
  }

  public updateInputControl(value: Date | null) {
    if (this.inputControl.value !== value) {
      this.inputControl.setValue(value);
    }
  }

  public getDateValuesAsStrings(date: Date) {
    const day = this.getDateValueString(date.getDate());
    const month = this.getDateValueString(date.getMonth() + 1);
    const year = this.getDateValueString(date.getFullYear());
    const hour = this.getDateValueString(date.getHours());
    const minute = this.getDateValueString(date.getMinutes());
    const second = this.getDateValueString(date.getSeconds());
    return date != null ? { day, month, year, hour, minute, second } : null;
  }

  public getDateValueString(value: number) {
    if (value != null && value < 10) {
      return '0' + value.toString();
    }
    return value.toString();
  }

  public setValueFromControl(inputControlValue: any) {
    const dateValues =
      inputControlValue != null ? this.getDateValuesAsStrings(inputControlValue) : null;
    if (this.inputType === DateTimeInputType.Date) {
      this.displayDateInput = true;
      if (dateValues != null) {
        this.value = dateValues.month + dateValues.day + dateValues.year;
      }
    }
    if (this.inputType === DateTimeInputType.Time) {
      this.displayTimeInput = true;
      if (dateValues != null) {
        this.value = dateValues.hour + dateValues.minute;
      }
    }
    if (this.inputType === DateTimeInputType.DateTime) {
      this.displayDateTimeInput = true;
      if (dateValues != null) {
        this.value =
          dateValues.month + dateValues.day + dateValues.year + dateValues.hour + dateValues.minute;
      }
    }
    if (this.inputType === DateTimeInputType.DateTimeWithSeconds) {
      this.displayDateTimeWithSecondsInput = true;
      if (dateValues != null) {
        this.value =
          dateValues.month +
          dateValues.day +
          dateValues.year +
          dateValues.hour +
          dateValues.minute +
          dateValues.second;
      }
    }
  }

  public onBlurEvent() {
    this.blurEvent.emit();
  }

  public onFocusEvent() {
    this.focusEvent.emit();
  }

  public setInvalidDate() {
    this.isValidDateTime = false;
    this.updateInputControl(null);
    this.valueChange.emit(null);
  }

  public setValidDate(date: Date) {
    this.isValidDateTime = true;
    this.updateInputControl(date);
    this.valueChange.emit(date);
  }

  public resetValidity() {
    this.isValidDateTime = true;
  }
}
